import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import {
  formatAccountingAmount,
  getAddOnRateTypeLabel,
  settlementSheetCardSx,
} from './settlementSheetDisplay';
import { SettlementSheetTotalAmounts } from './settlementSheetTotalAmounts';

interface TotalsRowProps {
  amount: number;
  currencyFormatter: (value: number) => string;
  isSecondary?: boolean;
  label: string;
}

function TotalsRow({
  amount,
  currencyFormatter,
  isSecondary = false,
  label,
}: TotalsRowProps) {
  const variant = isSecondary ? 'caption' : 'body2';

  return (
    <Box
      sx={{
        color: isSecondary ? 'text.secondary' : 'text.primary',
        display: 'flex',
        gap: 2,
        justifyContent: 'space-between',
        pl: isSecondary ? 1 : 0,
      }}
    >
      <Typography variant={variant}>{label}</Typography>
      <Typography variant={variant} sx={{ fontWeight: isSecondary ? 400 : 600 }}>
        {formatAccountingAmount(amount, currencyFormatter)}
      </Typography>
    </Box>
  );
}

interface SettlementSheetTotalsProps {
  currencyFormatter: (value: number) => string;
  totals: SettlementSheetTotalAmounts;
}

export default function SettlementSheetTotals({
  currencyFormatter,
  totals,
}: SettlementSheetTotalsProps) {
  return (
    <Box
      data-test-id="settlement-sheet-totals"
      sx={(theme) => ({
        ...settlementSheetCardSx(theme),
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minWidth: '280px',
      })}
    >
      <TotalsRow
        amount={totals.subtotal}
        currencyFormatter={currencyFormatter}
        label={t('approvals.driver_pay.settlements.subtotal')}
      />
      <TotalsRow
        amount={totals.addOnsTotal}
        currencyFormatter={currencyFormatter}
        label={t('approvals.driver_pay.settlements.add_ons')}
      />
      {totals.addOnSubtotals.map((addOnSubtotal) => (
        <TotalsRow
          key={addOnSubtotal.key}
          amount={addOnSubtotal.totalAmount}
          currencyFormatter={currencyFormatter}
          isSecondary
          label={t('approvals.driver_pay.settlements.sheet.add_on_subtotal', {
            name: addOnSubtotal.name,
            rateType: getAddOnRateTypeLabel(addOnSubtotal.rateType),
          })}
        />
      ))}
      {totals.taxTotal ? (
        <TotalsRow
          amount={totals.taxTotal}
          currencyFormatter={currencyFormatter}
          label={t('approvals.driver_pay.settlements.tax')}
        />
      ) : null}
      <Divider sx={{ my: 0.5 }} />
      <TotalsRow
        amount={totals.total}
        currencyFormatter={currencyFormatter}
        label={t('approvals.driver_pay.tabs.driver_pay.total')}
      />
      {totals.hasTotalsPendingSave && (
        <Typography
          color="text.secondary"
          data-test-id="settlement-sheet-totals-pending"
          variant="caption"
        >
          {t('approvals.driver_pay.settlements.sheet.totals_pending_save')}
        </Typography>
      )}
    </Box>
  );
}
